import React, { useEffect, useRef, useState } from 'react';
import PriceLevelWidget from './PriceLevelWidgets';
import './OrderBookWidgets.css';
import { truncateDecimal } from '../../utils/math';
import { useOrderBook } from '../../app/providers';

const OrderBookWidget = ({ selectedStock }) => {
    const { bidVolumes, askVolumes, hasOrderBook } = useOrderBook(selectedStock);

    const sortedBids = Object.entries(bidVolumes)
        .map(([price, quantity]) => ({ P: parseFloat(price), Q: quantity }))
        .sort((a, b) => b.P - a.P);

    const sortedAsks = Object.entries(askVolumes)
        .map(([price, quantity]) => ({ P: parseFloat(price), Q: quantity }))
        .sort((a, b) => a.P - b.P);

    const spread =
        sortedAsks.length > 0 && sortedBids.length > 0
            ? `${truncateDecimal(sortedAsks[0].P - sortedBids[0].P, 2)} (${truncateDecimal((100 * (sortedAsks[0].P - sortedBids[0].P)) / ((sortedAsks[0].P + sortedBids[0].P) / 2), 2)}%)`
            : `N/A`;

    // Depth bar: scale each row relative to the max visible quantity
    const maxBidQty = sortedBids.reduce((m, b) => Math.max(m, b.Q as number), 1);
    const maxAskQty = sortedAsks.reduce((m, a) => Math.max(m, a.Q as number), 1);

    const [autoCenter, setAutoCenter] = useState(true);

    const asksRef = useRef(null);

    useEffect(() => {
        if (asksRef.current && autoCenter) {
            asksRef.current.scrollTop = asksRef.current.scrollHeight - asksRef.current.clientHeight;
        }
    }, [sortedAsks, autoCenter]);

    const bidsRef = useRef(null);

    useEffect(() => {
        if (bidsRef.current && autoCenter) {
            bidsRef.current.scrollTop = 0;
        }
    }, [sortedBids, autoCenter]);

    useEffect(() => {
        const handleBidsScroll = () => {
            if (bidsRef.current && bidsRef.current.scrollTop !== 0) {
                setAutoCenter((prev) => (prev ? !prev : prev));
            }
        };

        const handleAsksScroll = () => {
            if (
                asksRef.current &&
                asksRef.current.scrollTop !==
                    asksRef.current.scrollHeight - asksRef.current.clientHeight
            ) {
                setAutoCenter((prev) => (prev ? !prev : prev));
            }
        };

        const bidsDivElement = bidsRef.current;
        if (bidsDivElement) {
            bidsDivElement.addEventListener('scroll', handleBidsScroll);
        }

        const asksDivElement = asksRef.current;
        if (asksDivElement) {
            asksDivElement.addEventListener('scroll', handleAsksScroll);
        }

        return () => {
            if (bidsDivElement) {
                bidsDivElement.removeEventListener('scroll', handleBidsScroll);
            }
            if (asksDivElement) {
                asksDivElement.removeEventListener('scroll', handleAsksScroll);
            }
        };
    }, [autoCenter]);

    const handleReCenter = () => {
        setAutoCenter((prev) => (prev ? prev : !prev));
    };

    return (
        <div className="order-book-widget">
            <h4>Order Book · {selectedStock}</h4>
            {!hasOrderBook || (sortedBids.length === 0 && sortedAsks.length === 0) ? (
                <p className="empty-order-book">Empty Order Book</p>
            ) : (
                <>
                    <div className="column-headers">
                        <span className="header price-header">Price</span>
                        <span className="header quantity-header">Qty</span>
                    </div>

                    <div ref={asksRef} className="order-book-scrollable-asks">
                        {sortedAsks.length > 0 ? (
                            sortedAsks.map((ask, index) => (
                                <PriceLevelWidget
                                    key={`ask-${index}`}
                                    price={truncateDecimal(ask.P, 2)}
                                    quantity={truncateDecimal(ask.Q, 2)}
                                    className="ask-row"
                                    depthPct={Math.min(100, ((ask.Q as number) / maxAskQty) * 100)}
                                />
                            ))
                        ) : (
                            <p className="empty-section">No asks</p>
                        )}
                    </div>

                    {/* Spread & Re-Center row */}
                    <PriceLevelWidget
                        price={`Spread: ${spread}`}
                        quantity={''}
                        amount={
                            <button
                                onClick={handleReCenter}
                                style={{
                                    backgroundColor: autoCenter ? '#2b3139' : '#1e2329',
                                    color: autoCenter ? '#eaecef' : '#848e9c',
                                    padding: '3px 8px',
                                    border: '1px solid #2b3139',
                                    cursor: 'pointer',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {autoCenter ? '● CENTER' : '○ CENTER'}
                            </button>
                        }
                        className="spread-row"
                    />

                    <div ref={bidsRef} className="order-book-scrollable-bids">
                        {sortedBids.length > 0 ? (
                            sortedBids.map((bid, index) => (
                                <PriceLevelWidget
                                    key={`bid-${index}`}
                                    price={truncateDecimal(bid.P, 2)}
                                    quantity={truncateDecimal(bid.Q, 2)}
                                    className="bid-row"
                                    depthPct={Math.min(100, ((bid.Q as number) / maxBidQty) * 100)}
                                />
                            ))
                        ) : (
                            <p className="empty-section">No bids</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default OrderBookWidget;
